import React, { useState } from "react";
import Tesseract from "tesseract.js";

interface MatchItem {
  name: string;
  url: string;
  file: File;
}

export default function CertificateFinder() {
  const [files, setFiles] = useState<File[]>([]);
  const [searchName, setSearchName] = useState("");
  const [matches, setMatches] = useState<MatchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [skipped, setSkipped] = useState<number>(0);

  const cleanupMatches = () => {
    setMatches((current) => {
      current.forEach((match) => URL.revokeObjectURL(match.url));
      return [];
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    cleanupMatches();
    setSkipped(0);
    setFiles(Array.from(e.target.files ?? []));
  };

  const handleSearch = async () => {
    if (!searchName.trim()) return;
    setLoading(true);
    cleanupMatches();

    const found: MatchItem[] = [];
    let skippedCount = 0;

    for (const file of files) {
      // Only OCR images in-browser; PDFs should be exported to images first
      if (!file.type.startsWith("image/")) {
        skippedCount += 1;
        continue;
      }

      const image = URL.createObjectURL(file);
      try {
        const result = await Tesseract.recognize(image, "eng", {
          logger: () => {},
        });
        const text = (result.data.text || "").toLowerCase();
        if (text.includes(searchName.toLowerCase())) {
          found.push({ name: file.name, url: image, file });
        } else {
          URL.revokeObjectURL(image);
        }
      } catch (err) {
        console.error(`Error scanning ${file.name}:`, err);
        URL.revokeObjectURL(image);
      }
    }

    // If only one match is found, auto-download it
    if (found.length === 1) {
      const [singleMatch] = found;
      const a = document.createElement("a");
      a.href = singleMatch.url;
      a.download = singleMatch.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(singleMatch.url);
      setFiles([]);
      setLoading(false);
      setSkipped(skippedCount);
      setSearchName("");
      return;
    }

    setMatches(found);
    setSkipped(skippedCount);
    setLoading(false);
  };

  const handleDownload = (match: MatchItem) => {
    const a = document.createElement("a");
    a.href = match.url;
    a.download = match.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(match.url);
    setMatches((current) => current.filter((item) => item.url !== match.url));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-100 flex flex-col items-center justify-center px-4">
      <div className="max-w-xl w-full bg-white/70 backdrop-blur-md shadow-lg rounded-2xl p-8 border border-gray-200">
        <h1 className="text-3xl font-bold mb-2 text-gray-800 tracking-tight">
          Certificate Finder
        </h1>
        <p className="text-gray-500 mb-6">
          Upload your certificates and find yours instantly by typing your name.
        </p>

        <div className="flex flex-col items-center space-y-4">
          <input
            type="file"
            accept="image/png,image/jpeg,application/pdf,image/webp"
            multiple
            onChange={handleFileUpload}
            className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
          />

          <div className="flex w-full space-x-2">
            <input
              type="text"
              placeholder="Enter your name..."
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            />
            <button
              onClick={handleSearch}
              disabled={loading || files.length === 0}
              className={`px-5 py-2 rounded-lg text-white font-medium transition ${
                loading || files.length === 0
                  ? "bg-blue-300"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {loading ? "Scanning..." : "Search"}
            </button>
          </div>

          {skipped > 0 && (
            <p className="text-xs text-gray-500">
              Skipped {skipped} non-image file(s). Export PDFs as PNG/JPG before
              scanning in-browser.
            </p>
          )}
        </div>

        {matches.length > 0 && (
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">
              Matches found
            </h2>
            <ul className="space-y-2 max-h-60 overflow-y-auto">
              {matches.map((match, idx) => (
                <li
                  key={idx}
                  className="p-3 bg-gray-50 rounded-lg flex justify-between items-center shadow-sm"
                >
                  <span className="truncate text-gray-800 font-medium">
                    {match.name}
                  </span>
                  <button
                    onClick={() => handleDownload(match)}
                    className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                  >
                    Download
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!loading && matches.length === 0 && searchName && (
          <p className="mt-6 text-gray-400 italic">
            No matching certificate found.
          </p>
        )}
      </div>

      <footer className="mt-8 text-gray-400 text-sm">
        Made with care by{" "}
        <span className="font-medium text-gray-600">Your Team</span>
      </footer>
    </div>
  );
}
